import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import './component-cart-items.js';
import './component-cart-quantity-selector.js';

/**
 * BRIXIES cart drawer behavior.
 *
 * The drawer opens exactly once after a successful add-to-cart. For adds coming
 * from a quick-add modal we wait until that modal has closed before opening the
 * cart, so focus restoration and animations cannot fight each other.
 *
 * The cart-items engine is imported here intentionally. Our custom empty drawer
 * does not render the native cart-products snippet, which normally loads that
 * engine. Without the import, the first add from an empty cart can wait forever
 * for <cart-items-component> to be defined, leaving both count and drawer stale.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  connectedCallback() {
    super.connectedCallback();

    this.#syncEmptyStateMarker();

    // Capture phase makes direct Product Card adds just as reliable as PDP and
    // quick-add submissions, even if another component handles the bubbling event.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, true);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, true);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Horizon detects empty -> filled cart transitions through this marker. Our
   * custom empty markup predates the native marker, so add it at runtime while
   * retaining the custom BRIXIES empty-state markup.
   */
  #syncEmptyStateMarker() {
    const cartItems = this.querySelector('cart-items-component');
    if (!cartItems) return;

    const isEmpty = cartItems.querySelector('.brx-cart-drawer__empty-wrap') !== null;
    if (!isEmpty || cartItems.querySelector('[data-cart-drawer-empty]')) return;

    const emptyContent = cartItems.querySelector('.brx-cart-drawer__scroll') ?? cartItems.firstElementChild;
    emptyContent?.setAttribute('data-cart-drawer-empty', '');
  }

  #handleDrawerOpen = () => {
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (!this.hasAttribute('auto-open') || event.action !== 'add') return;

    this.#syncEmptyStateMarker();

    const sourceDialog = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog[open]') : null
    );

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        const openDrawer = () => {
          // On the first add, Horizon replaces the empty cart markup. Opening
          // before that replacement can restart the dialog's entrance mid-flight.
          // Wait until the filled markup is mounted, then paint once before
          // opening the live drawer instance.
          let frames = 0;
          const openWhenReady = () => {
            const drawer = /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
              document.querySelector('theme-drawer#cart-drawer')
            );
            if (!drawer) return;
            if (drawer.isOpen) return;

            const isStillEmpty = drawer.querySelector(
              '.brx-cart-drawer--empty, .brx-cart-drawer__empty-wrap, [data-cart-drawer-empty]'
            );
            if (isStillEmpty && frames++ < 20) {
              requestAnimationFrame(openWhenReady);
              return;
            }

            // Preserve existing behavior if Shopify hydration is delayed.
            requestAnimationFrame(() => {
              const liveDrawer = /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
                document.querySelector('theme-drawer#cart-drawer')
              );
              if (liveDrawer && !liveDrawer.isOpen) liveDrawer.open();
            });
          };
          requestAnimationFrame(openWhenReady);
        };

        // A quick-add dialog closes itself after the same successful cart event.
        // Wait for that close so the cart animation only runs once afterwards.
        if (sourceDialog?.open && !sourceDialog.contains(this)) {
          sourceDialog.addEventListener('close', openDrawer, { once: true });
        } else {
          openDrawer();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
